import { createApp } from 'vue';
import App from './App.vue';
// through the alias, so mock mode can swap the module for its sign-in stub
import { ensureSignedIn } from '@/auth/msal';
import { showSignInFailure } from '@/auth/signInFailure';
import router from './router';

import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import DrawerService from './plugins/drawerService';

import '@/assets/styles.scss';
import '@/assets/tailwind.css';
import { RegleVuePlugin } from '@regle/core';

const app = createApp(App);

//zeres brand green: 500 is the website primary (#2f773a), 400 the bright accent used on dark surfaces
const MyPreset = definePreset(Aura, {
    semantic: {
        primary: {
            50: '#f1f9f1',
            100: '#ddf0de',
            200: '#bce3bf',
            300: '#90d296',
            400: '#56cb5a',
            500: '#2f773a',
            600: '#276531',
            700: '#205229',
            800: '#1a4322',
            900: '#15371c',
            950: '#0b1e0f'
        },
        overlay: {
            modal: {
                borderRadius: '1.5rem'
            },
            popover: {
                borderRadius: '10px'
            }
        },
        colorScheme: {
            light: {
                surface: {
                    0: 'color-mix(in srgb, {primary.950}, white 100%)',
                    50: 'color-mix(in srgb, {primary.950}, white 95%)',
                    100: 'color-mix(in srgb, {primary.950}, white 90%)',
                    200: 'color-mix(in srgb, {primary.950}, white 80%)',
                    300: 'color-mix(in srgb, {primary.950}, white 70%)',
                    400: 'color-mix(in srgb, {primary.950}, white 60%)',
                    500: 'color-mix(in srgb, {primary.950}, white 50%)',
                    600: 'color-mix(in srgb, {primary.950}, white 40%)',
                    700: 'color-mix(in srgb, {primary.950}, white 30%)',
                    800: 'color-mix(in srgb, {primary.950}, white 20%)',
                    900: 'color-mix(in srgb, {primary.950}, white 10%)',
                    950: 'color-mix(in srgb, {primary.950}, white 5%)'
                }
            },
            dark: {
                surface: {
                    0: 'color-mix(in srgb, var(--surface-ground), white 100%)',
                    50: 'color-mix(in srgb, var(--surface-ground), white 95%)',
                    100: 'color-mix(in srgb, var(--surface-ground), white 90%)',
                    200: 'color-mix(in srgb, var(--surface-ground), white 80%)',
                    300: 'color-mix(in srgb, var(--surface-ground), white 70%)',
                    400: 'color-mix(in srgb, var(--surface-ground), white 60%)',
                    500: 'color-mix(in srgb, var(--surface-ground), white 50%)',
                    600: 'color-mix(in srgb, var(--surface-ground), white 40%)',
                    700: 'color-mix(in srgb, var(--surface-ground), white 30%)',
                    800: 'color-mix(in srgb, var(--surface-ground), white 20%)',
                    900: 'color-mix(in srgb, var(--surface-ground), white 10%)',
                    950: 'color-mix(in srgb, var(--surface-ground), white 5%)'
                }
            }
        }
    }
});

app.use(PrimeVue, {
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.app-dark'
        }
    },
    // Dutch convention, not Dutch language: DatePicker falls back to dateFormat when a component
    // sets none, and the week starts on Monday. Every other key in PrimeVue's locale object is UI
    // text, so it keeps its English default alongside the app's own labels.
    locale: {
        dateFormat: 'dd-mm-yy',
        firstDayOfWeek: 1
    }
});
app.use(RegleVuePlugin);
app.use(ToastService);
app.use(ConfirmationService);
app.use(DrawerService);

async function bootstrap() {
    // Mock mode only (`npm run dev:mock`): the MSW worker answers /api/* from mocks/ and must be
    // running before the first request. Vite inlines MODE, so in every other build this branch
    // and its import are dead code and nothing under mocks/ is bundled.
    if (import.meta.env.MODE === 'mock') {
        const { startMockApi } = await import('../mocks/browser');
        await startMockApi();
    }

    // Sign in through Entra before mounting; the api client relies on the signed-in account
    await ensureSignedIn();
    // Installing the router starts the first navigation, whose guard reads /me for the user's
    // permissions; that needs the signed-in account and, in mock mode, the running worker.
    app.use(router);
    app.mount('#app');
}

// Without this the app never mounts and index.html's preloading spinner turns forever, with the
// reason in a console line the next redirect erases.
bootstrap().catch(showSignInFailure);
